/**
 * Template-component tile source for the legacy (Backoffice docx) triage flow.
 *
 * The automated flow groups HUMAN `ResearchPlanItem`s; the legacy flow groups
 * Django-template `ScopeComponent`s over the report's subjects × jurisdictions.
 * This module adapts a fetched template + subjects (each carrying its extracted
 * jurisdictions) into `ResearchPlanItemForPanel`-shaped tiles so `ScopeGrid` /
 * `AssignmentCard` render unchanged, and derives the create payload
 * (`{scope_component_slug, subject_id, jurisdiction_code}`) for a selection.
 *
 * Grid shape (mirrors the automated flow): Subject → Jurisdiction → component
 * tiles. So each subject's components are fanned out once per jurisdiction the
 * subject carries (extracted + any the analyst adds). A subject with no
 * jurisdiction still shows its components under an "Unspecified" group so the
 * analyst can proceed or add one.
 *
 * Tile identity: `slug::subjectId::jurisdictionCode` — deterministic, so the
 * membership of a created assignment item (keyed the same way in ScopeGrid)
 * lines up with the tile, mirroring how automated membership keys by
 * `planItemId`. The jurisdiction code rides `dimensions.jurisdiction` (the same
 * slot the automated tiles use) so the create-payload mapping can read it back.
 */

import type { BackofficeTemplate } from '@/types';
import type { ResearchPlanItemForPanel } from '@/types/research-plan';

/** Deterministic tile id for a (component, subject, jurisdiction) unit. */
export function templateTileId(
  slug: string,
  subjectId: string | null,
  jurisdictionCode: string | null
): string {
  return `tmpl::${slug}::${subjectId ?? ''}::${jurisdictionCode ?? ''}`;
}

export interface TemplateTileJurisdiction {
  /** ISO-2 code (e.g. 'KE'). */
  code: string;
  /** Display name (e.g. 'Kenya'); falls back to the code when unknown. */
  name: string;
}

export interface TemplateTileSubject {
  id: string;
  name: string;
  /** Extracted + analyst-added jurisdictions for this subject. */
  jurisdictions: TemplateTileJurisdiction[];
}

/**
 * Build the tile list: for each subject, for each of its jurisdictions, render
 * every template component as a tile. A subject with no jurisdictions still
 * renders its components (under the "Unspecified" group) so the flow isn't
 * blocked — the analyst can add a jurisdiction to surface them grouped.
 */
export function buildTemplateTiles(
  template: BackofficeTemplate | null,
  subjects: TemplateTileSubject[]
): ResearchPlanItemForPanel[] {
  if (!template) {
    return [];
  }
  const tiles: ResearchPlanItemForPanel[] = [];
  for (const subject of subjects) {
    // At least one "slot" per subject so components always render; each real
    // jurisdiction becomes its own group, and no-jurisdiction → one unset slot.
    const juris: (TemplateTileJurisdiction | null)[] =
      subject.jurisdictions.length > 0 ? subject.jurisdictions : [null];
    for (const j of juris) {
      for (const component of template.components) {
        const slug = component.slug ?? component.name;
        tiles.push({
          id: templateTileId(slug, subject.id, j?.code ?? null),
          item_type: 'human',
          status: 'pending',
          scope_component_slug: slug,
          scope_component_id: '',
          dimensions: j ? { jurisdiction: j.code } : {},
          subject_id: subject.id,
          subject_name: subject.name,
          jurisdiction_name: j?.name ?? null,
          subjurisdiction_name: null,
          requirement_data: null,
        });
      }
    }
  }
  return tiles;
}

/** The create-payload template item a tile maps to. */
export interface TemplateItemPayload {
  scope_component_slug: string;
  subject_id: string | null;
  jurisdiction_code: string | null;
}

/** Map selected tile ids back to the create payload, via the tile list. */
export function tilesToTemplateItems(
  tileIds: string[],
  tiles: ResearchPlanItemForPanel[]
): TemplateItemPayload[] {
  const byId = new Map(tiles.map((t) => [t.id, t]));
  const out: TemplateItemPayload[] = [];
  for (const id of tileIds) {
    const tile = byId.get(id);
    if (tile) {
      const jurisdictionCode =
        typeof tile.dimensions?.jurisdiction === 'string' ? tile.dimensions.jurisdiction : null;
      out.push({
        scope_component_slug: tile.scope_component_slug,
        subject_id: tile.subject_id,
        jurisdiction_code: jurisdictionCode,
      });
    }
  }
  return out;
}
